import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      
      // 1. Obtener todas las actividades y sus inscripciones
      const { data: activitiesData, error: activitiesError } = await supabase
        .from('activities')
        .select(`
          *,
          student_activities(
            student_id,
            status,
            students(name)
          )
        `)
        .order('name');
      if (activitiesError) throw activitiesError;

      // 2. Obtener el número de alumnos con asistencia registrada hoy por actividad
      const today = new Date().toISOString().split('T')[0];
      
      const { data: attendancesData, error: attendancesError } = await supabase
        .from('attendances')
        .select('activity_id, student_id')
        .eq('date', today)
        .eq('status', 'present');
      if (attendancesError) throw attendancesError;

      // 3. Agrupar por actividad
      const attendanceCountByActivity: Record<string, Set<string>> = {};
      (attendancesData || []).forEach((att) => {
        if (!attendanceCountByActivity[att.activity_id]) {
          attendanceCountByActivity[att.activity_id] = new Set();
        }
        attendanceCountByActivity[att.activity_id].add(att.student_id);
      });

      // 4. Mapear actividades con el número de alumnos presentes hoy
      const activitiesWithStudents = activitiesData?.map(activity => ({
        ...activity,
        currentStudents: activity.student_activities?.filter(sa => sa.status === 'active')?.length || 0,
        maxStudents: activity.max_capacity || 20,
        instructor: 'Instructor', // Por ahora placeholder
        time: `${activity.start_time} - ${activity.end_time}`
      }));
      
      setActivities(activitiesWithStudents || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { activities, loading, error, refetch: loadActivities };
};

export const useStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('students')
        .select(`
          *,
          student_activities(
            id,
            activity_id,
            status,
            activities(id, name, start_time, end_time, days_of_week, requires_day_selection)
          ),
          attendances(
            id,
            activity_id,
            date,
            status,
            time
          )
        `)
        .order('name');
      
      if (error) {
        throw error;
      }
      
      // Optimización: agrupar los student_id y activity_id para hacer una sola consulta
      const studentActivityPairs: {studentId: string, activityId: string}[] = [];
      
      (data || []).forEach(student => {
        (student.student_activities || []).forEach((sa: any) => {
          if (sa.status === 'active' && sa.activities?.requires_day_selection) {
            studentActivityPairs.push({
              studentId: student.id,
              activityId: sa.activity_id
            });
          }
        });
      });
      
      
      
      // Si hay pares estudiante-actividad, hacer una sola consulta para todos los horarios
      let schedulesByStudentActivity: Record<string, Record<string, number[]>> = {};
      
      if (studentActivityPairs.length > 0) {
        // Extraer todos los student_ids únicos
        const studentIds = [...new Set(studentActivityPairs.map(pair => pair.studentId))];
        
        // Extraer todos los activity_ids únicos
        const activityIds = [...new Set(studentActivityPairs.map(pair => pair.activityId))];
        
        
        
        // Hacer una sola consulta para todos los horarios
        const { data: allSchedules, error: scheduleError } = await supabase
          .from('student_activity_schedule')
          .select('student_id, activity_id, day_of_week')
          .in('student_id', studentIds)
          .in('activity_id', activityIds);
        
        if (scheduleError) {
          console.error('Error al cargar horarios:', scheduleError);
        } else {
          
          // Agrupar horarios por student_id y activity_id
          schedulesByStudentActivity = {};
          
          (allSchedules || []).forEach(schedule => {
            if (!schedulesByStudentActivity[schedule.student_id]) {
              schedulesByStudentActivity[schedule.student_id] = {};
            }
            
            if (!schedulesByStudentActivity[schedule.student_id][schedule.activity_id]) {
              schedulesByStudentActivity[schedule.student_id][schedule.activity_id] = [];
            }
            
            schedulesByStudentActivity[schedule.student_id][schedule.activity_id].push(schedule.day_of_week);
          });
        }
      }
      
      // Combinar los datos
      const studentsWithSchedules = (data || []).map(student => {
        const activitiesWithSchedules = (student.student_activities || []).map((sa: any) => {
          if (sa.status !== 'active' || !sa.activities?.requires_day_selection) return sa;
          
          // Obtener horarios de la estructura agrupada
          const schedule = schedulesByStudentActivity[student.id]?.[sa.activity_id] || [];
          
          return {
            ...sa,
            activities: {
              ...sa.activities,
              schedule
            }
          };
        });
        
        return {
          ...student,
          student_activities: activitiesWithSchedules
        };
      });
      
      setStudents(studentsWithSchedules || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { students, loading, error, refetch: loadStudents };
};

export const useAttendanceStats = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    presentStudents: 0,
    totalActivities: 0,
    activeActivities: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Total estudiantes
      const { count: totalStudents } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true });

      // Estudiantes presentes hoy
      const today = new Date().toISOString().split('T')[0];
      const { count: presentStudents } = await supabase
        .from('attendances')
        .select('*', { count: 'exact', head: true })
        .eq('date', today)
        .eq('status', 'present');

      // Total actividades
      const { count: totalActivities } = await supabase
        .from('activities')
        .select('*', { count: 'exact', head: true });

      // Actividades activas
      const { count: activeActivities } = await supabase
        .from('activities')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      setStats({
        totalStudents: totalStudents || 0,
        presentStudents: presentStudents || 0,
        totalActivities: totalActivities || 0,
        activeActivities: activeActivities || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return { stats, loading, refetch: loadStats };
};

// CRUD Functions for Students
export const createStudent = async (studentData: {
  name: string;
  class: string;
  email?: string;
  phone?: string;
}) => {
  const { data, error } = await supabase
    .from('students')
    .insert(studentData)
    .select();
  
  if (error) throw error;
  return data;
};

export const updateStudent = async (id: string, studentData: {
  name?: string;
  class?: string;
  email?: string;
  phone?: string;
}) => {
  const { data, error } = await supabase
    .from('students')
    .update({ ...studentData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const deleteStudent = async (id: string) => {
  // Marcar inscripciones como inactivas
  await supabase
    .from('student_activities')
    .update({ status: 'inactive', updated_at: new Date().toISOString() })
    .eq('student_id', id);
  
  // Eliminar asistencias
  await supabase.from('attendances').delete().eq('student_id', id);
  
  // Luego eliminar el estudiante
  const { error } = await supabase
    .from('students')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// CRUD Functions for Activities
export const createActivity = async (activityData: {
  name: string;
  description?: string;
  start_time: string;
  end_time: string;
  days_of_week: number[];
  max_capacity?: number;
  status?: string;
}) => {
  const { data, error } = await supabase
    .from('activities')
    .insert(activityData)
    .select();
  
  if (error) throw error;
  return data;
};

export const updateActivity = async (id: string, activityData: {
  name?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  days_of_week?: number[];
  max_capacity?: number;
  status?: string;
}) => {
  const { data, error } = await supabase
    .from('activities')
    .update({ ...activityData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const deleteActivity = async (id: string) => {
  // Primero eliminar inscripciones y asistencias
  await supabase.from('student_activities').delete().eq('activity_id', id);
  await supabase.from('attendances').delete().eq('activity_id', id);
  
  // Luego eliminar la actividad
  const { error } = await supabase
    .from('activities')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};